import * as R from 'ramda'
import { adapter, initialState, State } from './message.state'
import { Action, createReducer, on } from '@ngrx/store'
import {
  AddMessage,
  AddMessageComplete,
  DeleteMessageComplete,
  DeleteMessagesComplete,
  GetBookingMessagesComplete,
  GetGuestMessagesComplete,
  GetMessagesByIdsComplete,
  LoadLatestMessages,
  LoadLatestMessagesComplete,
  LoadUnreadCount,
  LoadUnreadCountComplete,
  MarkAsReadComplete,
  MarkAsUnreadComplete,
  MarkOneMessageAsReadComplete,
  MoveMessageComplete,
  MoveMessagesInConversationComplete,
  NewMessagesReceived,
  RemoveMessageFromFeedComplete,
  RemoveMessagesFromFeedComplete,
  UpdateMessageComplete,
} from '@tv3/store/message/message.actions'
import { ActionFailed } from '@tokeet-frontend/tv3-platform'

export const reducer = createReducer(
  initialState,
  on(LoadLatestMessagesComplete, (state, { messages }) =>
    adapter.addMany(messages, R.merge(state, { isLoaded: true, isLoading: false }))
  ),
  on(GetBookingMessagesComplete, (state, { messages, bookingIds }) =>
    adapter.addMany(
      messages,
      adapter.removeMany((t) => bookingIds.includes(t.inquiryId), state)
    )
  ),
  on(GetGuestMessagesComplete, (state, { messages, guestId }) =>
    adapter.addMany(
      messages,
      adapter.removeMany((t) => t.guestId === guestId, state)
    )
  ),
  on(GetMessagesByIdsComplete, (state, { messages }) => adapter.addMany(messages, state)),
  on(NewMessagesReceived, (state, { messages }) => adapter.addMany(messages, state)),
  on(AddMessage, (state) => ({ ...state, isAddingMessage: true })),
  on(AddMessageComplete, (state, { message }) => adapter.addOne(message, { ...state, isAddingMessage: false })),
  on(MoveMessagesInConversationComplete, (state, { messages }) => adapter.upsertMany(messages, state)),
  on(MarkAsReadComplete, (state, { updates }) =>
    adapter.updateMany(updates, {
      ...state,
      unreadCount: Math.max(0, state.unreadCount - updates.length),
    })
  ),
  on(MarkAsUnreadComplete, (state, { updates }) =>
    adapter.updateMany(updates, {
      ...state,
      unreadCount: state.unreadCount + updates.length,
    })
  ),
  on(DeleteMessageComplete, (state, { message }) => adapter.removeOne(message.id, state)),
  on(RemoveMessageFromFeedComplete, (state, { message }) => adapter.removeOne(message.id, state)),
  on(MarkOneMessageAsReadComplete, (state, { update }) =>
    adapter.updateOne(update, {
      ...state,
      unreadCount: Math.max(0, state.unreadCount - 1),
    })
  ),
  on(MoveMessageComplete, UpdateMessageComplete, (state, { update }) => adapter.updateOne(update, state)),
  on(DeleteMessagesComplete, (state, { items }) =>
    adapter.removeMany(
      items.map((t) => t.message.id),
      state
    )
  ),
  on(RemoveMessagesFromFeedComplete, (state, { messages }) =>
    adapter.removeMany(
      messages.map((t) => t.id),
      state
    )
  ),
  on(LoadLatestMessages, (state) => ({ ...state, isLoading: true })),
  on(LoadUnreadCount, (state) => ({ ...state, isUnreadCountLoading: true })),
  on(LoadUnreadCountComplete, (state, { count }) => ({
    ...state,
    unreadCount: count,
    isUnreadCountLoaded: true,
    isUnreadCountLoading: false,
  })),
  on(ActionFailed, (state) => ({ ...state, isLoading: false, isAddingMessage: false, isUnreadCountLoading: false }))
)

export function messageReducer(state: State | undefined, action: Action) {
  return reducer(state, action)
}

export const { selectIds, selectEntities, selectAll, selectTotal } = adapter.getSelectors()
